import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import EventIcon from '@mui/icons-material/Event';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import GavelIcon from '@mui/icons-material/Gavel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NavigationIcon from '@mui/icons-material/Navigation';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockIcon from '@mui/icons-material/Lock';
import ShieldIcon from '@mui/icons-material/Shield';
import { useNavigate } from 'react-router-dom';

const guideFeatures = [
  {
    id: 'dashboard',
    title: 'Dashboard & Analytics',
    category: 'Administration',
    icon: <DashboardIcon sx={{ color: '#4CA1AF' }} />,
    path: '/admin/dashboard',
    screenshot: '/assets/guide/dashboard.png',
    purpose: 'Real-time bird’s-eye view of ministry metrics, active prayers, recent members, and urgent moderation tasks.',
    summary: 'Monitor community engagement, total prayer intercessions, and daily user activity at a glance.',
    annotations: [
      { id: 1, title: 'Key Metric Cards', desc: 'Real-time totals of Users, Organizations, Active Prayers & Pending Approvals', x: 42, y: 35, arrowDir: 'down' },
      { id: 2, title: 'Quick Action Hub', desc: 'Direct shortcuts to Add New Prayer, Review Requests, and configure Settings', x: 83, y: 64, arrowDir: 'left' },
      { id: 3, title: 'Organization Switcher', desc: 'Easily switch context between multiple campuses or churches', x: 9, y: 15, arrowDir: 'right' },
    ],
    steps: [
      { num: 1, title: 'Inspect Overview Metrics', text: 'Check the 5 headline cards for user growth, active intercessions, and pending approvals.' },
      { num: 2, title: 'Use Quick Actions', text: 'Quickly trigger common administrative tasks like creating special prayers or system config.' },
      { num: 3, title: 'Monitor Recent Activity', text: 'Review live community prayer submissions and member activities in the timeline below.' },
    ],
    tips: 'Review the dashboard daily to keep track of new prayer requests awaiting moderation before Sunday worship services.',
  },
  {
    id: 'prayers',
    title: 'Prayers Hub (Public & Private)',
    category: 'Prayers & Devotion',
    icon: <Diversity3Icon sx={{ color: '#4CA1AF' }} />,
    path: '/admin/prayers',
    screenshot: '/assets/guide/prayers.png',
    purpose: 'Manage communal prayer walls and steward private intercessory petitions with absolute confidentiality.',
    summary: 'Organize prayer petitions by category (Healing, Family, Missions, Thanksgiving), moderate public requests, and assign private prayers to intercessory teams.',
    annotations: [
      { id: 1, title: 'Prayer Mode Tabs', desc: 'Switch seamlessly between Public Prayers, Private Confidential Prayers, and Categories', x: 26, y: 22, arrowDir: 'down' },
      { id: 2, title: 'Category & Status Filter', desc: 'Filter prayers by Category (Healing, Missions, etc.), Status (Approved, Pending), or Church', x: 62, y: 49, arrowDir: 'down' },
      { id: 3, title: 'New Prayer Button', desc: 'Create pastoral prayer announcements or post urgent prayer petitions', x: 92, y: 49, arrowDir: 'left' },
      { id: 4, title: 'Prayer Moderation Cards', desc: 'Review member requests, view full details, and approve or delete inappropriate posts', x: 30, y: 68, arrowDir: 'up' },
    ],
    steps: [
      { num: 1, title: 'Filter by Category & Status', text: 'Select "Pending" in the Status dropdown to inspect newly submitted requests requiring review.' },
      { num: 2, title: 'Review & Verify Content', text: 'Ensure prayers do not contain private medical details, telephone numbers, or unwholesome speech.' },
      { num: 3, title: 'Approve or Take Action', text: 'Click the view or checkmark icons to approve requests to the live prayer feed.' },
    ],
    tips: 'Ensure member privacy by checking that medical or personal contact details are not exposed in public prayer titles.',
  },
  {
    id: 'prayer-closet',
    title: 'Prayer Closet (Targeted Initiatives)',
    category: 'Prayers & Devotion',
    icon: <LocalFireDepartmentIcon sx={{ color: '#F97316' }} />,
    path: '/admin/prayer-closet',
    screenshot: '/assets/guide/prayer_closet.png',
    purpose: 'Mobilize believers into structured, disciplined prayer commitments for nations, revival, and church initiatives.',
    summary: 'Create custom prayer campaigns with defined timelines, Scripture readings, and prayer slots from 15 minutes to year-long initiatives.',
    annotations: [
      { id: 1, title: 'Campaign Categories', desc: 'Organize prayer initiatives by focus areas such as Revival, Healing, or National Fasting', x: 25, y: 25, arrowDir: 'down' },
      { id: 2, title: 'Create Special Prayer', desc: 'Launch a new multi-day prayer initiative with daily scripture readings and prayer topics', x: 88, y: 46, arrowDir: 'left' },
      { id: 3, title: 'Subscriber Tracking', desc: 'Monitor how many believers have committed to praying daily during the campaign', x: 50, y: 68, arrowDir: 'up' },
    ],
    steps: [
      { num: 1, title: 'Create Campaign Initiative', text: 'Click "+ Add Special Prayer", define the title, theme, start date, and prayer duration.' },
      { num: 2, title: 'Set Scripture Guidelines', text: 'Add daily Bible verses, reflection notes, and specific intercessory prayer bullets.' },
      { num: 3, title: 'Engage Intercessors', text: 'Track daily subscribers and celebrate prayer milestones with community notifications.' },
    ],
    tips: 'Use Prayer Closet campaigns during church revival weeks, 21-day fasts, or national prayer vigils.',
  },
  {
    id: 'users',
    title: 'Users & Roles Management',
    category: 'Administration',
    icon: <PeopleIcon sx={{ color: '#38BDF8' }} />,
    path: '/admin/users',
    screenshot: '/assets/guide/users.png',
    purpose: 'Manage church member directory, staff privileges, role assignments, and account verifications.',
    summary: 'Search users, assign organizational roles (Admin, Staff, App User), approve pending member registrations, and manage permissions.',
    annotations: [
      { id: 1, title: 'Search & Quick Filters', desc: 'Filter directory by Active, Pending, Inactive, or search by name, email, or church', x: 52, y: 26, arrowDir: 'down' },
      { id: 2, title: 'Role & Status Badges', desc: 'Clear badges displaying user role (Org Admin, Staff, Member) and Verification status', x: 67, y: 55, arrowDir: 'left' },
      { id: 3, title: 'User Action Buttons', desc: 'Edit member profile, assign church roles, activate accounts, or suspend access', x: 88, y: 55, arrowDir: 'left' },
    ],
    steps: [
      { num: 1, title: 'Locate Member Account', text: 'Search by full name or email address, or filter by "Pending" for new registrations.' },
      { num: 2, title: 'Assign Organization Role', text: 'Click the role management icon to designate trusted leaders as Organization Staff or Admins.' },
      { num: 3, title: 'Verify & Activate', text: 'Click the green checkmark icon to verify member credentials and activate community privileges.' },
    ],
    tips: 'Regularly audit staff roles to ensure only active ministry coordinators have elevated administrative access.',
  },
  {
    id: 'share-and-accept',
    title: 'Share & Accept (Generosity Ministry)',
    category: 'Community & Fellowship',
    icon: <VolunteerActivismIcon sx={{ color: '#EC4899' }} />,
    path: '/admin/share-and-accept',
    screenshot: '/assets/guide/share_accept.png',
    purpose: 'Christian benevolence platform embodying practical love by sharing physical items, books, clothing, and household blessings.',
    summary: 'Review and moderate items offered by givers, approve member item requests, and celebrate answered needs through generosity testimonies.',
    annotations: [
      { id: 1, title: 'Benevolence Tabs', desc: 'Browse Giver Items, Item Requests, New Requests, and Generosity Testimonies', x: 30, y: 24, arrowDir: 'down' },
      { id: 2, title: 'Moderation Filter', desc: 'Filter items by Condition (New, Good), Category, or Offering Organization', x: 60, y: 48, arrowDir: 'down' },
      { id: 3, title: 'Listing Approvals', desc: 'Inspect photos and item descriptions to ensure they are safe, wholesome, and free of commercial sales', x: 45, y: 68, arrowDir: 'up' },
    ],
    steps: [
      { num: 1, title: 'Moderate Giver Items', text: 'Review offered items to verify they are free charitable donations and appropriate for church families.' },
      { num: 2, title: 'Review Member Requests', text: 'Help match members facing hardship with available furniture, clothing, or ministry supplies.' },
      { num: 3, title: 'Publish Testimonies', text: 'Share stories of answered prayer and God’s benevolence to encourage church generosity.' },
    ],
    tips: 'Verify that all items are offered freely in Christian charity without commercial transactions or hidden fees.',
  },
  {
    id: 'ride-connect',
    title: 'Ride Connect (Transportation Fellowship)',
    category: 'Community & Fellowship',
    icon: <DriveEtaIcon sx={{ color: '#3B82F6' }} />,
    path: '/admin/ride-connect',
    screenshot: '/assets/guide/ride_connect.png',
    purpose: 'Coordinate safe, friendly church transportation for seniors, newcomers, and members needing a ride to service.',
    summary: 'Manage volunteer drivers, coordinate pickup locations, and foster fellowship on the way to church gatherings.',
    annotations: [
      { id: 1, title: 'Driver Roster', desc: 'View verified church volunteer drivers, seating capacities, and vehicle descriptions', x: 26, y: 25, arrowDir: 'down' },
      { id: 2, title: 'Filter by Service', desc: 'Filter ride requests by Sunday morning services, midweek youth, or prayer vigils', x: 62, y: 48, arrowDir: 'down' },
      { id: 3, title: 'Driver Verification', desc: 'Verify volunteer driver credentials to ensure passenger safety and mutual accountability', x: 88, y: 62, arrowDir: 'left' },
    ],
    steps: [
      { num: 1, title: 'Onboard Volunteer Drivers', text: 'Verify driver license details, vehicle passenger capacity, and active church membership.' },
      { num: 2, title: 'Assign Rides', text: 'Connect elderly or non-driving members with nearby drivers in the same neighborhood.' },
      { num: 3, title: 'Confirm Pickups', text: 'Ensure pickup times are coordinated so everyone arrives relaxed and ready to worship.' },
    ],
    tips: 'Encourage drivers to arrive early and introduce new riders to greeters upon arrival at church.',
  },
  {
    id: 'songs',
    title: 'Worship Songs & Chords',
    category: 'Worship & Content',
    icon: <LibraryMusicIcon sx={{ color: '#A855F7' }} />,
    path: '/admin/songs',
    screenshot: '/assets/guide/songs.png',
    purpose: 'Curate your church worship catalog with multilingual lyrics, chord sheets, and downloadable PDF lead sheets.',
    summary: 'Add worship songs in English, Spanish, Hindi, Telugu, and other languages. Attach PDF chord sheets for your praise team musicians.',
    annotations: [
      { id: 1, title: 'Search & Language Filter', desc: 'Search by song title, author, or filter by specific worship language', x: 45, y: 34, arrowDir: 'down' },
      { id: 2, title: '+ Add New Song', desc: 'Upload new song lyrics, YouTube reference link, and chord sheets for the praise team', x: 88, y: 34, arrowDir: 'left' },
      { id: 3, title: 'Chords & Lyrics Viewer', desc: 'Manage lyric formatting, guitar chords, and downloadable PDF sheet music', x: 50, y: 65, arrowDir: 'up' },
    ],
    steps: [
      { num: 1, title: 'Add Song Details', text: 'Enter the song title, artist/composer, primary language, and lyrical theme.' },
      { num: 2, title: 'Paste Lyrics & Chords', text: 'Input lyrics with bracketed chords or upload PDF lead sheets for praise band musicians.' },
      { num: 3, title: 'Link YouTube Preview', text: 'Attach a reference audio link so vocalists can rehearse before weekly rehearsals.' },
    ],
    tips: 'Tag songs by liturgical themes (e.g., Communion, Praise, Cross, Holy Spirit) for quick Sunday setlist planning.',
  },
  {
    id: 'daily-devotions',
    title: 'Daily Devotions & Reflections',
    category: 'Worship & Content',
    icon: <MenuBookIcon sx={{ color: '#10B981' }} />,
    path: '/admin/daily-devotions',
    screenshot: '/assets/guide/daily_devotions.png',
    purpose: 'Nourish members spiritually every morning with Scripture of the day, pastoral commentary, and prayer prompts.',
    summary: 'Schedule and publish daily spiritual devotions that appear on member mobile app home screens.',
    annotations: [
      { id: 1, title: 'Calendar & Date Filter', desc: 'Schedule devotions in advance across dates and target organizations', x: 38, y: 28, arrowDir: 'down' },
      { id: 2, title: 'Rich Text Devotion Editor', desc: 'Compose the devotional message, scripture text, and inspiring pastoral reflections', x: 50, y: 55, arrowDir: 'up' },
      { id: 3, title: 'Publishing Status', desc: 'Review scheduled, drafted, and published daily reflections for your congregation', x: 85, y: 28, arrowDir: 'left' },
    ],
    steps: [
      { num: 1, title: 'Select Target Date', text: 'Pick the calendar date for the devotion to appear on member mobile apps.' },
      { num: 2, title: 'Add Scripture & Reflection', text: 'Input key Bible verses, author credit, and a 200–400 word reflection.' },
      { num: 3, title: 'Publish to Community', text: 'Save and activate so members receive their morning notification and devotional reading.' },
    ],
    tips: 'Prepare devotions in weekly batches every Monday to ensure seamless daily spiritual encouragement.',
  },
  {
    id: 'events',
    title: 'Events & Conferences',
    category: 'Community & Fellowship',
    icon: <EventIcon sx={{ color: '#EAB308' }} />,
    path: '/admin/events',
    screenshot: '/assets/guide/events.png',
    purpose: 'Promote upcoming church conferences, prayer vigils, seminars, and fellowship retreats.',
    summary: 'Manage event dates, banner graphics, venues, speaker profiles, and member RSVP attendance.',
    annotations: [
      { id: 1, title: 'Event Timeline', desc: 'Browse scheduled church conferences, youth retreats, and special prayer summits', x: 42, y: 35, arrowDir: 'down' },
      { id: 2, title: '+ Create Event', desc: 'Publish a new church gathering with date, venue, speaker details, and registration link', x: 88, y: 35, arrowDir: 'left' },
      { id: 3, title: 'RSVP & Attendee Info', desc: 'Track member attendance commitments and coordinate venue seating', x: 55, y: 65, arrowDir: 'up' },
    ],
    steps: [
      { num: 1, title: 'Create Event Details', text: 'Enter event title, start/end dates, location venue or online Zoom/YouTube link.' },
      { num: 2, title: 'Upload Event Poster', text: 'Attach a high-resolution banner image that showcases the conference theme.' },
      { num: 3, title: 'Publish & Notify', text: 'Publish the event so it appears in the mobile app events calendar for member RSVPs.' },
    ],
    tips: 'Include full address and Google Maps links for physical venues so visitors can find your sanctuary easily.',
  },
  {
    id: 'testimonies',
    title: 'Testimonies & Praise Reports',
    category: 'Community & Fellowship',
    icon: <SpeakerNotesIcon sx={{ color: '#14B8A6' }} />,
    path: '/admin/testimonies',
    screenshot: '/assets/guide/testimonies.png',
    purpose: 'Celebrate answered prayers and build church faith by sharing miraculous praise reports.',
    summary: 'Review member testimonies, verify accuracy, format inspiring faith stories, and feature them on the mobile app.',
    annotations: [
      { id: 1, title: 'Testimony Inbox', desc: 'View praise reports submitted by believers after their prayers were answered', x: 40, y: 38, arrowDir: 'down' },
      { id: 2, title: 'Review & Verify', desc: 'Inspect testimonies to ensure they glorify God and respect family confidentiality', x: 85, y: 55, arrowDir: 'left' },
      { id: 3, title: 'Featured Status', desc: 'Feature powerful praise reports to highlight on the mobile app home screen', x: 55, y: 70, arrowDir: 'up' },
    ],
    steps: [
      { num: 1, title: 'Review Praise Report', text: 'Read the submitted testimony and verify that names and medical references are handled respectfully.' },
      { num: 2, title: 'Tag Corresponding Prayer', text: 'Link the testimony to the original prayer request to show God’s faithfulness.' },
      { num: 3, title: 'Publish to Church Feed', text: 'Approve the testimony to inspire faith throughout the global prayer network.' },
    ],
    tips: 'Featuring answered prayer testimonies directly underneath prayer requests significantly increases community intercession.',
  },
  {
    id: 'organizations',
    title: 'Organizations & Campuses',
    category: 'Administration',
    icon: <BusinessIcon sx={{ color: '#6366F1' }} />,
    path: '/admin/organizations',
    screenshot: '/assets/guide/organizations.png',
    purpose: 'Configure church campuses, affiliated Christian NGOs, regional fellowships, and branch ministries.',
    summary: 'Manage organizational branding, address details, administrative contacts, and multi-campus configurations.',
    annotations: [
      { id: 1, title: 'Campus Directory', desc: 'View all onboarded church branches, fellowships, and mission organizations', x: 42, y: 35, arrowDir: 'down' },
      { id: 2, title: '+ Register Organization', desc: 'Onboard a new church campus with branding, logo, and administrative credentials', x: 88, y: 35, arrowDir: 'left' },
      { id: 3, title: 'Campus Configuration', desc: 'Manage church contact information, social links, and pastor contact info', x: 60, y: 65, arrowDir: 'up' },
    ],
    steps: [
      { num: 1, title: 'Add New Church / Branch', text: 'Enter official organization name, church logo, denomination, and contact information.' },
      { num: 2, title: 'Designate Lead Admin', text: 'Assign the primary pastoral administrator responsible for managing the church account.' },
      { num: 3, title: 'Configure Campus Settings', text: 'Set church time zone, primary worship language, and community privacy preferences.' },
    ],
    tips: 'Keep contact phone numbers and office emails updated so intercessors can reach pastoral staff in emergencies.',
  },
  {
    id: 'settings',
    title: 'Settings & Mobile Customization',
    category: 'Administration',
    icon: <SettingsIcon sx={{ color: '#64748B' }} />,
    path: '/admin/settings',
    screenshot: '/assets/guide/settings.png',
    purpose: 'Central hub for church branding, live streaming YouTube links, faith statements, and approval rules.',
    summary: 'Customize the mobile app homepage banners, Sunday live stream feeds, bottom navigation tabs, and doctrinal statements.',
    annotations: [
      { id: 1, title: 'App Homepage Banners', desc: 'Configure top carousel announcements and promotional graphic banners', x: 30, y: 25, arrowDir: 'down' },
      { id: 2, title: 'Sunday Live Streaming', desc: 'Set your church YouTube live stream URL for Sunday morning video broadcasting', x: 30, y: 50, arrowDir: 'down' },
      { id: 3, title: 'Faith Statement & Rules', desc: 'Define your church’s doctrinal points and automated prayer moderation rules', x: 30, y: 75, arrowDir: 'down' },
    ],
    steps: [
      { num: 1, title: 'Configure Mobile Banners', text: 'Upload 16:9 banner slides for upcoming sermons, charity drives, or conference announcements.' },
      { num: 2, title: 'Embed Sunday Live Stream', text: 'Enter your church YouTube channel ID or stream URL so members can watch services directly.' },
      { num: 3, title: 'Review Moderation Rules', text: 'Choose between auto-approving trusted member prayers or requiring manual pastoral sign-off.' },
    ],
    tips: 'Update your YouTube live stream links weekly before Sunday morning services to ensure high mobile viewership.',
  },
  {
    id: 'admin-agreement',
    title: 'Admin Agreement & Compliance',
    category: 'Administration',
    icon: <GavelIcon sx={{ color: '#F59E0B' }} />,
    path: '/admin/admin-agreement',
    screenshot: '/assets/guide/admin_agreement.png',
    purpose: 'Super Admin compliance tracker and administrator code of conduct for prayer confidentiality and stewardship.',
    summary: 'Track which administrators have accepted the GPN Operating Agreement and who is still pending acceptance. Verify digital certificates.',
    annotations: [
      { id: 1, title: 'Live Compliance Metrics', desc: 'Real-time counters for Total Staff, Signed Agreements, and Outstanding Pending Agreements', x: 42, y: 30, arrowDir: 'down' },
      { id: 2, title: 'Staff Acceptance Table', desc: 'Lists every organization admin with their verification code or pending status badge', x: 62, y: 62, arrowDir: 'left' },
      { id: 3, title: 'Reminder & Verification', desc: 'Send automated email reminders to pending staff or inspect digital certificate dialogs', x: 88, y: 62, arrowDir: 'left' },
    ],
    steps: [
      { num: 1, title: 'Monitor Compliance Rate', text: 'Review the 4 metric cards to identify how many church admins have completed their agreement.' },
      { num: 2, title: 'Send Reminders to Pending Staff', text: 'Click "Remind" next to any administrator with an amber "Pending" chip to trigger notification.' },
      { num: 3, title: 'Inspect Certificates', text: 'Click "Certificate" on any verified staff member to review their digital signature and ID.' },
    ],
    tips: 'Super Admins are completely exempt from submitting the agreement and possess oversight authority across all organizations.',
  },
];

const categories = ['All Features', 'Prayers & Devotion', 'Community & Fellowship', 'Worship & Content', 'Administration'];

const faqs = [
  {
    q: 'How do I approve or activate a new member registration?',
    a: 'Navigate to "Users" from the sidebar menu. Filter by status "Pending" or search for the user name. Click the activation toggle or action button to approve the account.',
  },
  {
    q: 'What is the difference between Public Prayers and Private Prayers?',
    a: 'Public Prayers are visible to all members of your organization on the prayer wall to encourage collective prayer. Private Prayers are confidential and restricted strictly to pastoral leaders and designated intercessors.',
  },
  {
    q: 'Why do I see an "Admin Agreement Pending" alert in the header?',
    a: 'All GPN administrators must accept the sacred stewardship and confidentiality agreement. Click the alert or navigate to "Admin Agreement" in the footer, review the terms, and provide your electronic signature to clear the alert.',
  },
  {
    q: 'How do I switch between multiple churches or organizations?',
    a: 'If you oversee multiple campuses, click the "Switch Org" button beneath your logo in the left sidebar or click your profile avatar at the top right and select "Switch Organization".',
  },
  {
    q: 'How can our church stream Sunday services on the mobile app?',
    a: 'Go to Settings > Sunday Worship & Bible Study. Enter your church YouTube Channel ID or live stream video URL. Members will be able to watch live directly within the GPN mobile app.',
  },
];

// Helper component for interactive annotated screenshot with callout pins and arrow marks
const AnnotatedScreenshot = ({ feature, showAnnotations, onZoom, privacyBlur = false }) => {
  const [activePin, setActivePin] = useState(null);

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 2.5,
        overflow: 'hidden',
        border: '1px solid rgba(76, 161, 175, 0.35)',
        background: '#0F172A',
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.5)',
      }}
    >
      {/* Real Screenshot Image with optional extra blur filter */}
      <Box
        component="img"
        src={feature.screenshot}
        alt={feature.title}
        sx={{
          width: '100%',
          display: 'block',
          objectFit: 'cover',
          filter: privacyBlur ? 'blur(3px) brightness(0.95)' : (activePin ? 'brightness(0.92)' : 'none'),
          transition: 'filter 0.3s',
        }}
      />

      {/* Privacy Shield Badge */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          display: 'flex',
          gap: 0.8,
          alignItems: 'center',
          background: 'rgba(15, 23, 42, 0.88)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          px: 1.2,
          py: 0.5,
          border: '1px solid rgba(56, 189, 248, 0.4)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
          zIndex: 5,
        }}
      >
        <LockIcon sx={{ fontSize: 13, color: '#38BDF8' }} />
        <Typography variant="caption" sx={{ color: '#E2E8F0', fontWeight: 600, fontSize: '0.72rem', letterSpacing: 0.3 }}>
          {privacyBlur ? 'Privacy Shield (Blurred) • Anonymized Data' : 'Privacy Protected • Anonymized Demo Data'}
        </Typography>
      </Box>

      {/* SVG Arrow Marks & Callout Overlay */}
      {showAnnotations && feature.annotations && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none',
          }}
        >
          {feature.annotations.map((pin) => {
            const isHovered = activePin === pin.id;
            return (
              <Box
                key={pin.id}
                sx={{
                  position: 'absolute',
                  left: `${pin.x}%`,
                  top: `${pin.y}%`,
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'auto',
                  cursor: 'pointer',
                  zIndex: isHovered ? 20 : 10,
                }}
                onMouseEnter={() => setActivePin(pin.id)}
                onMouseLeave={() => setActivePin(null)}
              >
                {/* Visual Arrow Pointer */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: pin.arrowDir === 'down' ? -28 : pin.arrowDir === 'up' ? 24 : -4,
                    left: pin.arrowDir === 'left' ? -28 : pin.arrowDir === 'right' ? 24 : -4,
                    transform:
                      pin.arrowDir === 'down'
                        ? 'rotate(180deg)'
                        : pin.arrowDir === 'left'
                        ? 'rotate(-90deg)'
                        : pin.arrowDir === 'right'
                        ? 'rotate(90deg)'
                        : 'rotate(0deg)',
                    color: isHovered ? '#38BDF8' : '#F59E0B',
                    transition: 'all 0.25s',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.8))',
                  }}
                >
                  <NavigationIcon sx={{ fontSize: 22 }} />
                </Box>

                {/* Numbered Glowing Badge Pin */}
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#38BDF8' }}>
                        #{pin.id} • {pin.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#E2E8F0', display: 'block' }}>
                        {pin.desc}
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="top"
                >
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: isHovered
                        ? 'linear-gradient(135deg, #0284C7 0%, #38BDF8 100%)'
                        : 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                      border: '2px solid #FFFFFF',
                      color: isHovered ? '#FFFFFF' : '#1E293B',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 800,
                      fontSize: '0.82rem',
                      boxShadow: isHovered
                        ? '0 0 16px rgba(56, 189, 248, 0.9)'
                        : '0 0 10px rgba(245, 158, 11, 0.7)',
                      transform: isHovered ? 'scale(1.2)' : 'scale(1)',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {pin.id}
                  </Box>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Top Overlay Actions: Zoom Button & Annotation Indicator */}
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          background: 'rgba(15, 23, 42, 0.85)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          p: 0.5,
          border: '1px solid rgba(255, 255, 255, 0.15)',
        }}
      >
        <Tooltip title="View Fullscreen Screenshot">
          <IconButton
            size="small"
            onClick={onZoom}
            sx={{
              color: '#FFFFFF',
              '&:hover': { color: '#38BDF8', background: 'rgba(255,255,255,0.1)' },
            }}
          >
            <ZoomInIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Bottom Subtle Caption */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          px: 2,
          py: 0.8,
          background: 'linear-gradient(180deg, transparent 0%, rgba(15, 23, 42, 0.9) 100%)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.72rem' }}>
          Live GPN Admin Portal UI • Interactive Callout Arrows
        </Typography>
        <Typography variant="caption" sx={{ color: '#4CA1AF', fontWeight: 600, fontSize: '0.72rem' }}>
          Click Zoom to expand
        </Typography>
      </Box>
    </Box>
  );
};

const Guide = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Features');
  const urlParams = new URLSearchParams(window.location.search);
  const [layoutMode, setLayoutMode] = useState(urlParams.get('view') === 'grid' ? 'grid' : 'showcase');
  const [selectedFeatureId, setSelectedFeatureId] = useState(urlParams.get('feature') || 'dashboard');
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [privacyBlur, setPrivacyBlur] = useState(urlParams.get('blur') === 'true');
  const [zoomedScreenshot, setZoomedScreenshot] = useState(null);

  const filteredFeatures = useMemo(() => {
    return guideFeatures.filter((item) => {
      const matchesCategory = selectedCategory === 'All Features' || item.category === selectedCategory;
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch =
        !q ||
        item.title.toLowerCase().includes(q) ||
        item.purpose.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.steps.some((s) => s.title.toLowerCase().includes(q) || s.text.toLowerCase().includes(q));

      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, selectedCategory]);

  const activeFeature = useMemo(() => {
    return (
      filteredFeatures.find((f) => f.id === selectedFeatureId) ||
      filteredFeatures[0] ||
      guideFeatures[0]
    );
  }, [filteredFeatures, selectedFeatureId]);

  return (
    <Box sx={{ maxWidth: '1400px', mx: 'auto', pb: 6, px: { xs: 1.5, sm: 3 } }}>
      {/* Hero Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, md: 4 },
          mb: 3.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(76, 161, 175, 0.3)',
          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.35)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: 2.5,
                background: 'linear-gradient(135deg, rgba(76, 161, 175, 0.25) 0%, rgba(44, 62, 80, 0.5) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid rgba(76, 161, 175, 0.4)',
                boxShadow: '0 4px 12px rgba(76, 161, 175, 0.2)',
              }}
            >
              <MenuBookIcon sx={{ color: '#4CA1AF', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 800, fontSize: { xs: '1.5rem', sm: '1.9rem' } }}>
                GPN Admin Visual Guide & Tour
              </Typography>
              <Typography variant="caption" sx={{ color: '#4CA1AF', fontWeight: 600, fontSize: '0.85rem' }}>
                Annotated screenshots • Step-by-step instructions • Master every portal feature
              </Typography>
            </Box>
          </Box>

          {/* View Mode Toggle Button */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              background: 'rgba(15, 23, 42, 0.8)',
              p: 0.5,
              borderRadius: 2,
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Button
              size="small"
              startIcon={<ViewStreamIcon />}
              onClick={() => setLayoutMode('showcase')}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                color: layoutMode === 'showcase' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.6)',
                background: layoutMode === 'showcase' ? 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)' : 'transparent',
                boxShadow: layoutMode === 'showcase' ? '0 2px 8px rgba(76, 161, 175, 0.3)' : 'none',
              }}
            >
              Interactive Tour
            </Button>
            <Button
              size="small"
              startIcon={<ViewModuleIcon />}
              onClick={() => setLayoutMode('grid')}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.8rem',
                color: layoutMode === 'grid' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.6)',
                background: layoutMode === 'grid' ? 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)' : 'transparent',
                boxShadow: layoutMode === 'grid' ? '0 2px 8px rgba(76, 161, 175, 0.3)' : 'none',
              }}
            >
              Uniform Cards
            </Button>
          </Box>
        </Box>

        {/* Search Bar */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Search features, steps, and capabilities (e.g., Prayer Closet, User Roles, Songs, Live Stream, Agreement)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#4CA1AF' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              background: 'rgba(15, 23, 42, 0.7)',
              borderRadius: 2,
              '& .MuiInputBase-input': { color: '#FFFFFF', py: 1.3 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(76, 161, 175, 0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4CA1AF' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4CA1AF' },
            }}
          />
        </Box>

        {/* Category Filter Chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                clickable
                onClick={() => setSelectedCategory(cat)}
                sx={{
                  background: selectedCategory === cat ? 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)' : 'rgba(255, 255, 255, 0.08)',
                  color: selectedCategory === cat ? '#FFFFFF' : 'rgba(255, 255, 255, 0.75)',
                  fontWeight: selectedCategory === cat ? 700 : 500,
                  border: selectedCategory === cat ? '1px solid #4CA1AF' : '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    background: 'rgba(76, 161, 175, 0.25)',
                    color: '#FFFFFF',
                  },
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={privacyBlur}
                  onChange={(e) => setPrivacyBlur(e.target.checked)}
                  size="small"
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#38BDF8' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#38BDF8' },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LockIcon sx={{ fontSize: 14, color: privacyBlur ? '#38BDF8' : 'rgba(255,255,255,0.6)' }} />
                  <Typography variant="caption" sx={{ color: privacyBlur ? '#38BDF8' : 'rgba(255,255,255,0.7)', fontWeight: 600 }}>
                    Extra Privacy Blur
                  </Typography>
                </Box>
              }
            />

            {layoutMode === 'showcase' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showAnnotations}
                    onChange={(e) => setShowAnnotations(e.target.checked)}
                    size="small"
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#F59E0B' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#F59E0B' },
                    }}
                  />
                }
                label={
                  <Typography variant="caption" sx={{ color: '#FDE68A', fontWeight: 600 }}>
                    Show Arrow Marks & Callout Pins
                  </Typography>
                }
              />
            )}
          </Box>
        </Box>
      </Paper>

      {/* ------------------------------------------------------------- */}
      {/* MODE 1: INTERACTIVE TOUR & SHOWCASE VIEW (Recommended Default) */}
      {/* ------------------------------------------------------------- */}
      {layoutMode === 'showcase' && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'flex-start', width: '100%' }}>
          {/* Left Feature Navigator Column */}
          <Box sx={{ width: { xs: '100%', md: 310, lg: 340 }, flexShrink: 0 }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.85) 0%, rgba(15, 23, 42, 0.9) 100%)',
                border: '1px solid rgba(76, 161, 175, 0.2)',
                position: { md: 'sticky' },
                top: 85,
                maxHeight: { md: 'calc(100vh - 120px)' },
                overflowY: { md: 'auto' },
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(76, 161, 175, 0.3)', borderRadius: 3 },
              }}
            >
              <Typography variant="caption" sx={{ color: '#4CA1AF', fontWeight: 700, letterSpacing: '0.5px', px: 1, display: 'block', mb: 1 }}>
                SELECT A FEATURE TO EXPLORE ({filteredFeatures.length}):
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                {filteredFeatures.map((f) => {
                  const isSelected = activeFeature?.id === f.id;
                  return (
                    <Box
                      key={f.id}
                      onClick={() => setSelectedFeatureId(f.id)}
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: isSelected
                          ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.25) 0%, rgba(44, 62, 80, 0.35) 100%)'
                          : 'rgba(255, 255, 255, 0.03)',
                        border: isSelected
                          ? '1px solid #4CA1AF'
                          : '1px solid rgba(255, 255, 255, 0.06)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: 'rgba(76, 161, 175, 0.15)',
                          borderColor: 'rgba(76, 161, 175, 0.5)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>{f.icon}</Box>
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isSelected ? 700 : 500,
                              color: isSelected ? '#FFFFFF' : 'rgba(255, 255, 255, 0.85)',
                              fontSize: '0.875rem',
                              lineHeight: 1.2,
                            }}
                          >
                            {f.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.72rem' }}>
                            {f.category}
                          </Typography>
                        </Box>
                      </Box>
                      {isSelected && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#4CA1AF',
                            boxShadow: '0 0 8px #4CA1AF',
                          }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Box>

          {/* Right Main Showcase & Annotated Screenshot Column */}
          <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            {activeFeature && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Feature Header Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2.5, sm: 3 },
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
                    border: '1px solid rgba(76, 161, 175, 0.3)',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 2,
                      mb: 1.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          background: 'rgba(76, 161, 175, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          border: '1px solid rgba(76, 161, 175, 0.4)',
                        }}
                      >
                        {activeFeature.icon}
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#FFFFFF', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                          {activeFeature.title}
                        </Typography>
                        <Chip
                          label={activeFeature.category}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            background: 'rgba(76, 161, 175, 0.15)',
                            color: '#4CA1AF',
                            border: '1px solid rgba(76, 161, 175, 0.3)',
                            mt: 0.4,
                          }}
                        />
                      </Box>
                    </Box>

                    <Button
                      variant="contained"
                      size="small"
                      endIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
                      onClick={() => navigate(activeFeature.path)}
                      sx={{
                        background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        textTransform: 'none',
                        px: 2,
                        py: 0.8,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(76, 161, 175, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5AB3C2 0%, #34495E 100%)',
                        },
                      }}
                    >
                      Open in Portal
                    </Button>
                  </Box>

                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.92rem', lineHeight: 1.6 }}>
                    {activeFeature.purpose}
                  </Typography>
                </Paper>

                {/* Annotated Screenshot Card */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#FBBF24', fontWeight: 700, letterSpacing: '0.5px' }}>
                      AUTHENTIC PORTAL SCREENSHOT WITH ANNOTATION ARROWS:
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                      Hover over numbered pins to inspect controls
                    </Typography>
                  </Box>

                  <AnnotatedScreenshot
                    feature={activeFeature}
                    showAnnotations={showAnnotations}
                    privacyBlur={privacyBlur}
                    onZoom={() => setZoomedScreenshot(activeFeature)}
                  />
                </Box>

                {/* Numbered Feature Tour Cards Matching Arrow Pins */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 1.5, px: 0.5 }}>
                    Step-by-Step Instructions & Workflow:
                  </Typography>

                  <Grid container spacing={2}>
                    {activeFeature.steps.map((st) => (
                      <Grid item xs={12} sm={4} key={st.num}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            height: '100%',
                            borderRadius: 2.5,
                            background: 'rgba(30, 41, 59, 0.75)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            transition: 'all 0.2s',
                            '&:hover': {
                              borderColor: '#4CA1AF',
                              background: 'rgba(30, 41, 59, 0.95)',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                color: '#1A202C',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 800,
                                fontSize: '0.75rem',
                              }}
                            >
                              {st.num}
                            </Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: '0.85rem' }}>
                              {st.title}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '0.8rem', lineHeight: 1.5 }}>
                            {st.text}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                {/* Pro-Tip Box */}
                {activeFeature.tips && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(15, 23, 42, 0.6) 100%)',
                      border: '1px solid rgba(245, 158, 11, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                    }}
                  >
                    <LightbulbIcon sx={{ color: '#FBBF24', fontSize: 24, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: '#FEF3C7', fontSize: '0.85rem', lineHeight: 1.5 }}>
                      <strong>Administrative Best Practice:</strong> {activeFeature.tips}
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ------------------------------------------------------------- */}
      {/* MODE 2: UNIFORM CARD GRID (Equal Heights, Clean Proportions) */}
      {/* ------------------------------------------------------------- */}
      {layoutMode === 'grid' && (
        <Grid container spacing={3}>
          {filteredFeatures.map((item) => (
            <Grid item xs={12} sm={6} lg={4} key={item.id}>
              <Card
                elevation={0}
                sx={{
                  height: 480, // Explicit uniform height for all cards!
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
                  border: '1px solid rgba(76, 161, 175, 0.25)',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                  overflow: 'hidden',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    borderColor: '#4CA1AF',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 30px rgba(76, 161, 175, 0.2)',
                  },
                }}
              >
                {/* Uniform Screenshot Header (fixed height: 180px) */}
                <Box
                  sx={{
                    height: 180,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    background: '#0B1120',
                    cursor: 'pointer',
                  }}
                  onClick={() => setZoomedScreenshot(item)}
                >
                  <Box
                    component="img"
                    src={item.screenshot}
                    alt={item.title}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease',
                      '&:hover': { transform: 'scale(1.05)' },
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      px: 1,
                      py: 0.3,
                      borderRadius: 1.5,
                      background: 'rgba(15, 23, 42, 0.85)',
                      backdropFilter: 'blur(5px)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ color: '#FBBF24', fontWeight: 700, fontSize: '0.7rem' }}>
                      {item.annotations.length} Key Controls
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 8,
                      right: 8,
                      p: 0.5,
                      borderRadius: 1.5,
                      background: 'rgba(15, 23, 42, 0.8)',
                      color: '#FFFFFF',
                    }}
                  >
                    <ZoomInIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Box>

                {/* Card Content (fixed height flex) */}
                <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {item.icon}
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#FFFFFF', fontSize: '1rem' }}>
                        {item.title}
                      </Typography>
                    </Box>
                    <Chip
                      label={item.category}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        background: 'rgba(255, 255, 255, 0.08)',
                        color: 'rgba(255, 255, 255, 0.7)',
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255, 255, 255, 0.75)',
                      fontSize: '0.825rem',
                      lineHeight: 1.5,
                      mb: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {item.purpose}
                  </Typography>

                  {/* Highlights Bullet List (3 items) */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, mt: 'auto', mb: 1 }}>
                    {item.steps.slice(0, 2).map((st) => (
                      <Box key={st.num} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.8 }}>
                        <CheckCircleOutlineIcon sx={{ color: '#4CA1AF', fontSize: 15, mt: 0.2, flexShrink: 0 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.85)',
                            fontSize: '0.78rem',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          <strong>{st.title}:</strong> {st.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>

                <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

                {/* Card Actions Footer */}
                <CardActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
                  <Button
                    size="small"
                    startIcon={<ZoomInIcon />}
                    onClick={() => setZoomedScreenshot(item)}
                    sx={{
                      color: '#4CA1AF',
                      textTransform: 'none',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                    }}
                  >
                    Tour & Screenshot
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => navigate(item.path)}
                    sx={{
                      background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                      color: '#FFFFFF',
                      textTransform: 'none',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      borderRadius: 1.5,
                    }}
                  >
                    Open
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ------------------------------------------------------------- */}
      {/* FULLSCREEN SCREENSHOT & STEP-BY-STEP TOUR MODAL               */}
      {/* ------------------------------------------------------------- */}
      <Dialog
        open={Boolean(zoomedScreenshot)}
        onClose={() => setZoomedScreenshot(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            color: '#FFFFFF',
            borderRadius: 3,
            border: '1px solid rgba(76, 161, 175, 0.4)',
            boxShadow: '0 24px 60px rgba(0, 0, 0, 0.8)',
            maxHeight: '92vh',
          },
        }}
      >
        {zoomedScreenshot && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>{zoomedScreenshot.icon}</Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#FFFFFF' }}>
                    {zoomedScreenshot.title} • Feature Screenshot & Callout Guide
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#4CA1AF' }}>
                    {zoomedScreenshot.category} • {zoomedScreenshot.path}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={() => setZoomedScreenshot(null)} sx={{ color: 'rgba(255,255,255,0.7)' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)', p: { xs: 2, sm: 3 } }}>
              {/* Full Annotated Screenshot */}
              <Box sx={{ mb: 3 }}>
                <AnnotatedScreenshot
                  feature={zoomedScreenshot}
                  showAnnotations={true}
                  privacyBlur={privacyBlur}
                  onZoom={() => {}}
                />
              </Box>

              {/* Numbered Controls Tour */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 1.5 }}>
                Numbered Screen Controls:
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {zoomedScreenshot.annotations.map((pin) => (
                  <Grid item xs={12} sm={4} key={pin.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        height: '100%',
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.04)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box
                          sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: '#F59E0B',
                            color: '#1E293B',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            fontSize: '0.72rem',
                          }}
                        >
                          {pin.id}
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FBBF24', fontSize: '0.85rem' }}>
                          {pin.title}
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.4, display: 'block' }}>
                        {pin.desc}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Step By Step Instructions */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 1 }}>
                Recommended Administrator Workflow:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {zoomedScreenshot.steps.map((st) => (
                  <Box key={st.num} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                    <CheckCircleOutlineIcon sx={{ color: '#4CA1AF', fontSize: 18, mt: 0.2 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
                      <strong>{st.title}:</strong> {st.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
              <Button
                startIcon={<CloseIcon />}
                onClick={() => setZoomedScreenshot(null)}
                sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none' }}
              >
                Close Tour
              </Button>
              <Button
                variant="contained"
                endIcon={<OpenInNewIcon />}
                onClick={() => {
                  const targetPath = zoomedScreenshot.path;
                  setZoomedScreenshot(null);
                  navigate(targetPath);
                }}
                sx={{
                  background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Go to {zoomedScreenshot.title}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ------------------------------------------------------------- */}
      {/* FREQUENTLY ASKED QUESTIONS SECTION                            */}
      {/* ------------------------------------------------------------- */}
      <Box sx={{ mt: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <HelpOutlineIcon sx={{ color: '#4CA1AF', fontSize: 24 }} />
          <Typography variant="h5" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
            Frequently Asked Questions & Administrator Tips
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {faqs.map((faq, idx) => (
            <Accordion
              key={idx}
              sx={{
                background: 'rgba(30, 41, 59, 0.75)',
                border: '1px solid rgba(76, 161, 175, 0.2)',
                borderRadius: '12px !important',
                color: '#FFFFFF',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#4CA1AF' }} />}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.92rem' }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                {faq.a}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Guide;
